import { useSyncExternalStore } from 'react';

/**
 * Selected RegulatedEntity ("Aufsichtsobjekt") store — a tiny localStorage-backed
 * external store, mirroring how the active workspace is persisted
 * (`adjudon_active_workspace`). No provider needed.
 *
 * `null` = "Gesamtgruppe" (consolidated group view): the readiness Status tab
 * shows the roll-up; per-entity surfaces (Pflichten) fall back to the org's
 * default entity on the backend.
 *
 * The apiClient request interceptor reads `adjudon_active_entity` from
 * localStorage and sends it as `X-Entity-ID`, so every entity-scoped request is
 * automatically pinned. Hooks include the selected id in their queryKey so a
 * switch refetches.
 */

const STORAGE_KEY = 'adjudon_active_entity';

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function getSelectedEntityId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setSelectedEntityId(id: string | null): void {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota/availability errors — selection is best-effort */
  }
  emit();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** The currently selected entity id, or null for the consolidated group view. */
export function useSelectedEntityId(): string | null {
  return useSyncExternalStore(subscribe, getSelectedEntityId, () => null);
}
